import { isNotEmptyArray, isNotEmptyString, isNotSet, isNumber, isSet } from './formatChecker';

export const isRequired = (value: undefined | any) => (isSet(value) ? undefined : '此欄位為必填。');

export const stringRequired = (value: undefined | string) =>
  isSet(value) && isNotEmptyString(value) ? undefined : '此欄位為必填。';

// noinspection RegExpSimplifiable
export const phoneValidator = (value: undefined | string) =>
  isNotSet(value) ? undefined : /^[0-9]{9,10}$/.test(value) ? undefined : '請填入正確的手機號碼';

// noinspection RegExpSimplifiable
const emailReg =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const emailValidator = (value: undefined | string) =>
  isSet(value) && isNotEmptyString(value) ? (emailReg.test(value!) ? undefined : '請輸入正確的Email格式。') : undefined;

export const emailRequired = (value: undefined | string) =>
  isSet(value) && emailReg.test(value!) ? undefined : '請輸入正確的Email格式。';

// noinspection RegExpSimplifiable
export const contactPhoneValidator = (value: undefined | string = '') =>
  /^[0-9]{0,}-?[0-9]{0,}$/.test(value) ? undefined : '請輸入正確的電話號碼。';

export const multipleValueRequired = (value: Array<any> = []) =>
  isNotEmptyArray(value) ? undefined : '至少需要一個選項。';

// noinspection RegExpSimplifiable,RegExpRedundantEscape,RegExpUnnecessaryNonCapturingGroup
const URLReg =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

export const urlValidator = (value: undefined | string = '') =>
  URLReg.test(value) ? undefined : '請輸入正確的網址格式。';

export const digitalOnly = (value: undefined | string | number = 0) =>
  isNumber(value) || /^[0-9]+$/.test(value) ? undefined : '只允許填入數字';

// Detects strings made up entirely of Han-script characters (covers both Traditional and
// Simplified Chinese — Unicode doesn't split them by code point range, both live in the Han
// script).
//
// Preferred path: the native `\p{Script=Han}` Unicode property escape (ES2018). It operates
// on whole code points, so rare characters outside the BMP (surrogate pairs) are matched
// correctly, unlike a hand-rolled BMP-only range check. Built via the RegExp constructor
// (not a literal) so an unsupported engine throws at pattern-compile time — a catchable
// runtime error — rather than a parse-time SyntaxError that could break the whole chunk.
// Support gap: some browserslist targets in this project (KaiOS <3, Opera Mini) predate
// Unicode property escape support (Firefox 78 / Chrome 64 / Safari 11.1, 2018-2020), hence
// the fallback below.
let nativeChineseNameReg: RegExp | null;
try {
  nativeChineseNameReg = new RegExp('^\\p{Script=Han}{2,}$', 'u');
} catch {
  nativeChineseNameReg = null;
}

// Fallback: explicit Han-script Unicode block ranges, checked per code point. Used only when
// the native regex above isn't supported. Uses `codePointAt`/`Array.from` (baseline ES2015),
// not regex Unicode property escapes, so it works even on engines that don't support those.
//
// Ranges inlined from @scriptin/is-han (MIT License), https://github.com/scriptin/is-han —
// vendored rather than added as a dependency since it's a single static data table and the
// package itself has very little adoption/maintenance (2 GitHub stars, last published 2023).
type HanRange = [number] | [number, number];
const HAN_RANGES: Array<HanRange> = [
  [0x4e00, 0x9fff], // CJK Unified Ideographs
  [0x3400, 0x4dbf], // CJK Unified Ideographs Extension A
  [0x20000, 0x2a6df], // CJK Unified Ideographs Extension B
  [0x2a700, 0x2b739], // CJK Unified Ideographs Extension C
  [0x2b740, 0x2b81d], // CJK Unified Ideographs Extension D
  [0x2b820, 0x2cea1], // CJK Unified Ideographs Extension E
  [0x2ceb0, 0x2ebe0], // CJK Unified Ideographs Extension F
  [0x30000, 0x3134a], // CJK Unified Ideographs Extension G
  [0x31350, 0x323af], // CJK Unified Ideographs Extension H
  [0x2e80, 0x2eff], // CJK Radicals Supplement
  [0x2f00, 0x2fdf], // Kangxi Radicals
  [0xf900, 0xfaff], // CJK Compatibility Ideographs
  [0x2f800, 0x2fa1f], // CJK Compatibility Ideographs Supplement
];

export const isHanCodePoint = (codePoint: number): boolean =>
  HAN_RANGES.some(([start, end = start]) => codePoint >= start && codePoint <= end);

export const isAllHanFallback = (value: string): boolean => {
  const chars = Array.from(value); // code-point aware split — handles surrogate pairs correctly
  return chars.length >= 2 && chars.every((char) => isHanCodePoint(char.codePointAt(0)!));
};

export const chineseNameValidator = (value: undefined | string = '') => {
  const trimmed = (value ?? '').trim();
  const isValid = nativeChineseNameReg ? nativeChineseNameReg.test(trimmed) : isAllHanFallback(trimmed);
  return isValid ? undefined : '請輸入您的中文全名';
};
